import * as React from "react";
import { motion } from "framer-motion";
import { ChevronDownIcon, ChevronUpIcon, Cross2Icon, UploadIcon, PlusCircledIcon } from "@radix-ui/react-icons";
import { useDispatch, useSelector } from 'react-redux';
import { setTargetLanguages, clearUploadedFiles } from '../../store/chatSlice';
import { hideGlobalBlocking, showGlobalBlocking } from '../../store/loadingSlice';
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { RootState } from "../../store/store";
import { LanguagePicker } from "./LanguageSelector";

export interface ITranslationMessage {
  id?: string | number;
  timestamp: Date | string;
  role: string;
  content: string;
  error?: string; // optional error field to capture translation errors
  translationData?: Array<{ // optional translation file data for download buttons
  filename: string;
  content_base64: string;
  language: string;
}>;
}

interface TranslationModeProps {
  uploadedFiles: any[];
  awaitingFileSelection: boolean;
  chatPage?: boolean;
  iconSize?: number;
  translationFileInputRef: React.RefObject<HTMLInputElement | null>;
  bottomBubbleContainerRef: React.RefObject<HTMLDivElement | null>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  previousHeightRef: React.RefObject <number>;
  filesBeingProcessedRef: React.RefObject <boolean>;
  onHeightChange?: (height: number) => void;
  setAwaitingFileSelection: (value: boolean) => void;
  setSelectionWarning: (message: string | null) => void;
  setTranslationWarning?: (warning: { title: string; subtitle?: string; message: string } | null) => void;
  handleRemoveFile: (name: string) => void;
  handleTranslateDocuments: () => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearChatInputDragOverlay?: () => void;
}

// Format file size for display (e.g. "0.96 MB")
export const formatFileSize = (sizeInBytes?: number) => {
  if (!sizeInBytes && sizeInBytes !== 0) return '';
  const size = Number(sizeInBytes) || 0;
  if (size === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(size) / Math.log(1024));
  const value = size / Math.pow(1024, i);
  return `${value.toFixed(2)} ${units[i]}`;
}; 

export const TranslationMode: React.FC<TranslationModeProps> = ({
  uploadedFiles,
  awaitingFileSelection,
  chatPage,
  iconSize,
  translationFileInputRef,
  bottomBubbleContainerRef,
  textareaRef,
  previousHeightRef,
  filesBeingProcessedRef,
  onHeightChange,
  setAwaitingFileSelection,
  setSelectionWarning,
  setTranslationWarning,
  handleRemoveFile,
  handleTranslateDocuments,
  handleFileChange,
  clearChatInputDragOverlay,
}) => {
  const dispatch = useDispatch();
    const {lastTranslation, targetLanguages, isTranslating, glossaryFiles } = useSelector((state: RootState) => state.chat); 
    // Device detection using custom hook
    const { isMobile, isTablet, windowHeight } = useDeviceDetection();
    const [isCollapsed, setIsCollapsed] = React.useState({ filePreview: isMobile, languageSelection: isMobile});
    const [isMobileLangMenuOpen, setIsMobileLangMenuOpen] = React.useState(false);
    const [showGlossaryHint, setShowGlossaryHint] = React.useState(true);
    const [isDragOverUpload, setIsDragOverUpload] = React.useState(false);

    React.useEffect(() => {
      // show the hint when languages are selected; allow user to dismiss
      if (targetLanguages && targetLanguages.length > 0) setShowGlossaryHint(true);
    }, [targetLanguages]);

   // Check if this is a duplicate (same files + languages as last translation)
    const currentFileNames = uploadedFiles.map(f => f.name).sort();
    const currentLanguageCodes = targetLanguages.map(l => l.languageCode).sort();
    const isDuplicate = !!(lastTranslation &&
      JSON.stringify(lastTranslation.fileNames) === JSON.stringify(currentFileNames) &&
      JSON.stringify(lastTranslation.languageCodes) === JSON.stringify(currentLanguageCodes));
    const canTranslate = uploadedFiles.length > 0 && targetLanguages.length > 0 && !isDuplicate && !isTranslating;

    const renderCollapse = (type: 'filePreview' | 'languageSelection', isCollapsed: boolean) => {
      return (
        <div className={`flex items-center gap-2 ${isMobile && isCollapsed ? `shadow-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-2 ${type === 'languageSelection' && targetLanguages.length > 0 ? 'flex-col': ''}` : ''}`}>
             <div 
                className={`w-full flex justify-between items-center ${isMobile ? 'cursor-pointer' : ''} text-[#5E95ED]`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isMobile) {
                    // On mobile, open a full-screen language menu for selection
                    if (type === 'languageSelection') {
                      // Don't open language menu while a file is still being processed
                      if (!awaitingFileSelection) {
                        setIsMobileLangMenuOpen(true);
                      }
                      return;
                    }
                    setIsCollapsed(prev => ({ ...prev, [type]: !prev[type] }));
                  }}
              }                 
                aria-label={isCollapsed ? 'Expand translation details' : 'Collapse translation details'}
              >
                <span className="text-sm font-semibold-[500] text-[var(--color-text)]">
                  {type === 'filePreview' ? 'Upload up to 5 documents' : 'Choose up to 2 target languages'}
                </span>
                {!isMobile ? null : !isCollapsed ? (
                  <ChevronUpIcon width={iconSize} className="ml-auto flex-shrink-0"/>
                ) : (
                  <ChevronDownIcon width={iconSize} className="ml-auto flex-shrink-0"/>
                )}
              </div>
              {isMobile && targetLanguages && targetLanguages.length > 0 && type === 'languageSelection' && (
                <div className="mt-2 px-2 flex gap-2">
                  {targetLanguages.map((lang: any) => (
                    <button
                      key={lang.languageCode}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // remove language when chip clicked
                        handleTargetLanguageChange(targetLanguages.filter((l: any) => l.languageCode !== lang.languageCode));
                      }}
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-lg border border-[#5E95ED] bg-[var(--color-secondary)] text-sm text-[var(--color-text)]"
                      aria-label={`Remove ${lang.label}`}
                    >
                      <span className="truncate max-w-[140px]">{lang.label}</span>
                    </button>
                  ))}
                </div>
            )}
          </div>
      )
    }

    // Centralized handler so inline and mobile pickers behave identically
    const handleTargetLanguageChange = (newSelection: any[]) => {
      dispatch(setTargetLanguages(newSelection));
      // Measure height after selection changes
      requestAnimationFrame(() => {
        if (bottomBubbleContainerRef.current && onHeightChange) {
          const baseHeight = isTablet ? 64 : 56;
          const maxContentHeight = 128;
          const BASE_PAGE_OFFSET = !chatPage ? 110 : 0;
          const GREETING_INPUT_PADDING = !chatPage ? (iconSize || 32) : 0;
          const GREETING_SPACER = !chatPage ? 36 : 0;
          const EXTRA_GALLERY_OFFSET = BASE_PAGE_OFFSET + GREETING_INPUT_PADDING + GREETING_SPACER;
          const bottomBubbleHeight = bottomBubbleContainerRef.current.offsetHeight;
          let currentFinalHeight = baseHeight;
          if (textareaRef.current) {
            const currentScrollHeight = textareaRef.current.scrollHeight;
            const currentContentHeight = Math.max(currentScrollHeight, baseHeight);
            const currentNeedsScrolling = currentContentHeight > maxContentHeight;
            currentFinalHeight = currentNeedsScrolling ? maxContentHeight : currentContentHeight;
          }
          const totalComponentHeight = currentFinalHeight + bottomBubbleHeight + EXTRA_GALLERY_OFFSET;
          if (Math.abs(totalComponentHeight - previousHeightRef.current) > 5) {
            previousHeightRef.current = totalComponentHeight;
            onHeightChange(totalComponentHeight);
          }
        }
      });
    };

  return (
    <motion.div
      initial={{ opacity: 0, scaleY: 0 }}
      animate={{ opacity: 1, scaleY: 1 }}
      exit={{ opacity: 0, scaleY: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{ transformOrigin: 'bottom', maxHeight: windowHeight ? windowHeight - 100 : windowHeight, overflowY: 'auto' }}
    >
      {/* Horizontal 3-Column Layout on desktop, Vertical on mobile */}
      <div className={`flex ${isMobile ? 'flex-col gap-2' : 'flex-row gap-4 justify-center'}`}>
        {/* Step 1: Upload Documents */}
        <motion.div 
          className="flex flex-col p-2 gap-2 flex-1"
          layout
          initial={uploadedFiles.length === 0 ? { opacity: 0, scale: 0.95 } : false}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          style={{ maxWidth: isMobile ? '100%' : '45%', borderRadius: 8, border: isDragOverUpload ? '3px dashed #5E95ED' : '2px solid transparent', backgroundColor: isDragOverUpload ? 'rgba(94, 149, 237, 0.1)' : undefined, transition: 'border 0.15s, background-color 0.15s' }}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOverUpload(true); }}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOverUpload(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation();
            // only clear if leaving the column entirely (not entering a child)
            if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOverUpload(false);
          }}
          onDrop={(e) => {
            e.preventDefault(); e.stopPropagation(); setIsDragOverUpload(false);
            clearChatInputDragOverlay?.(); // clear ChatInput's isDraggingOver overlay (stopPropagation prevents ChatInput's handleDrop from clearing it)
            const files = e.dataTransfer?.files;
            if (files && files.length > 0 && translationFileInputRef.current) {
              setAwaitingFileSelection(true);
              filesBeingProcessedRef.current = true;
              const dataTransfer = new DataTransfer();
              Array.from(files).forEach(file => dataTransfer.items.add(file));
              translationFileInputRef.current.files = dataTransfer.files;
              const event = new Event('change', { bubbles: true });
              translationFileInputRef.current.dispatchEvent(event);
            }
          }}
        >
          {renderCollapse('filePreview', isCollapsed.filePreview)}
          {!isCollapsed.filePreview && (
            <div className="h-full flex flex-col gap-3 justify-center items-center">
              { uploadedFiles.length === 0 && !awaitingFileSelection && (
                <div
                  onClick={() => {
                    if (translationFileInputRef.current) {
                      translationFileInputRef.current.value = '';
                      filesBeingProcessedRef.current = false;
                      dispatch(showGlobalBlocking());

                      const handleFocus = () => {
                        dispatch(hideGlobalBlocking());
                        setAwaitingFileSelection(true);
                        window.removeEventListener('focus', handleFocus);
                      };
                      window.addEventListener('focus', handleFocus);

                      translationFileInputRef.current.click();
                    }
                  }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.backgroundColor = 'var(--hover)'; }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.backgroundColor = ''; }}
                  onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation(); e.currentTarget.style.backgroundColor = '';
                    setIsDragOverUpload(false); // stopPropagation prevents outer column's onDrop from clearing this
                    clearChatInputDragOverlay?.(); // clear ChatInput's isDraggingOver overlay
                    const files = e.dataTransfer?.files;
                    if (files && files.length > 0 && translationFileInputRef.current) {
                      setAwaitingFileSelection(true);
                      filesBeingProcessedRef.current = true;
                      const dataTransfer = new DataTransfer();
                      Array.from(files).forEach(file => dataTransfer.items.add(file));
                      translationFileInputRef.current.files = dataTransfer.files;
                      const event = new Event('change', { bubbles: true });
                      translationFileInputRef.current.dispatchEvent(event);
                    }
                  }}
                  className="flex flex-col items-center gap-1 px-6 py-2 border-2 border-dashed border-[var(--color-accent)] rounded-lg cursor-pointer hover:bg-[var(--hover)] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <UploadIcon width={16} height={16} className="text-[var(--color-text)]" />
                    <span className="text-sm font-medium text-[var(--color-text)]">Click to upload files</span>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--color-text)', opacity: 0.6 }}>or drag files here</span>
                </div>
              )}

              {uploadedFiles.length === 0 && awaitingFileSelection && (
                <div
                  onMouseDown={() => {
                    // Use onMouseDown: file read can complete between mousedown→mouseup,
                    // causing React to unmount this div before click fires.
                    // Also clear uploadedFiles: the file may have already been dispatched
                    // to Redux inside a FileReader onload macrotask before React re-rendered
                    // and while this cancel button was still visible.
                    dispatch(hideGlobalBlocking());
                    dispatch(clearUploadedFiles());
                    setTranslationWarning?.(null);
                    setAwaitingFileSelection(false);
                    filesBeingProcessedRef.current = false;
                  }}
                  className="flex flex-col items-center gap-1 px-6 py-3 border-2 border-dashed border-[var(--color-accent)] rounded-lg cursor-pointer hover:bg-[var(--hover)] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm font-medium text-[var(--color-text)]">Waiting for file<span className="animate-pulse">...</span></span>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--color-text)', opacity: 0.6 }}>Click to cancel</span>
                </div>
              )}

              {uploadedFiles.length === 0 && !awaitingFileSelection && (
                <div className="flex flex-col items-center gap-1 px-6 py-3 text-xs text-center text-[var(--color-text)] opacity-50">
                  <span>Input: 5 files, 10MB / 24h quota = 10 files</span>
                  <span>Supported formats: PDF, Word, Excel, PPTX, CSV</span>
                </div>
              )}

              {/* File Previews - stacked vertically */}
              {uploadedFiles.length > 0 && uploadedFiles.map((f, idx) => (
                <div
                  key={f.key || f.name}
                  className="flex items-center justify-between w-full px-4 py-2 rounded-md shadow-sm"
                  style={{
                    backgroundColor: document.documentElement.getAttribute('data-theme')?.startsWith('dark-') ? '#3a3a3a' : '#4b5563',
                    color: 'white'
                  }}
                  aria-label={`File ${f.name}`}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="font-semibold truncate text-sm" style={{ maxWidth: isMobile ? '220px' : '320px' }} title={f.name}>{f.name}</span>
                    <span className="text-xs" style={{ opacity: 0.8 }}>{f.size ? `File Size: ${formatFileSize(f.size)}` : ''}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(f.name)}
                    className="p-1 rounded-full hover:bg-white/10 flex items-center justify-center"
                    aria-label={`Remove uploaded file ${f.name}`}
                  >
                    <Cross2Icon width={24} height={24} />
                  </button>
                </div>
              ))}

              {/* Upload button below list (shows when less than 5 uploaded) */}
              {uploadedFiles.length > 0 && uploadedFiles.length < 5 && (
                <div className="flex justify-center pt-2 text-[#5E95ED]">
                  <button
                    type="button"
                    onClick={() => {
                      if (translationFileInputRef.current) {
                        translationFileInputRef.current.value = '';
                        filesBeingProcessedRef.current = false;
                        dispatch(showGlobalBlocking());
                        const handleFocus = () => {
                          dispatch(hideGlobalBlocking());
                          setAwaitingFileSelection(true);
                          window.removeEventListener('focus', handleFocus);
                        };
                        window.addEventListener('focus', handleFocus);
                        translationFileInputRef.current.click();
                      }
                    }}
                    className="inline-flex justify-center gap-2 px-4 py-2 rounded text-sm font-medium border border-transparent color-[#5E95ED] hover:bg-[var(--hover)] transition-colors"
                    aria-label="Upload another file"
                  >
                    <UploadIcon width={18} height={18} />
                    <span>Upload File</span>
                  </button>
                </div>
              )}

              {/* Glossary hint shown when a target language is selected */}
              {targetLanguages && targetLanguages.length > 0 && showGlossaryHint && (() => {
                const glossaryCount = targetLanguages.reduce((acc, l) => acc + (Array.isArray(glossaryFiles) ? glossaryFiles.filter(g => g.lang === l.languageCode).length : 0), 0);
                const langsWithFiles = targetLanguages.filter(l => Array.isArray(glossaryFiles) && glossaryFiles.some(g => g.lang === l.languageCode)).map(l => l.label);
                const formatLangs = (arr: string[]) => {
                  if (arr.length === 0) return '';
                  if (arr.length === 1) return arr[0];
                  if (arr.length === 2) return `${arr[0]} & ${arr[1]}`;
                  return `${arr.slice(0, -1).join(', ')} & ${arr[arr.length - 1]}`;
                };

                if (glossaryCount > 0) {
                  return (
                    <div className="w-full px-3 py-2 rounded-md bg-[#eef6ff] border-l-4 border-[#3b82f6] flex items-center gap-3">
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-black">{`${glossaryCount} Glossary file${glossaryCount > 1 ? 's' : ''} added!`}</div>
                        <div className="text-xs text-black opacity-90">{`Language: ${formatLangs(langsWithFiles)}`}</div>
                      </div>
                      <button
                        type="button"
                        aria-label="Dismiss glossary hint"
                        onClick={() => setShowGlossaryHint(false)}
                        className="p-1 rounded-full hover:bg-gray-100 flex items-center justify-center"
                      >
                        <Cross2Icon width={20} height={20} />
                      </button>
                    </div>
                  );
                }

                // default hint when no glossary uploaded yet
                return (
                  <div className="w-full px-3 py-2 rounded-md bg-yellow-50 border-l-4 border-yellow-300 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="text-xs font-medium font-semibold text-black">
                        Add a Glossary file by clicking the <PlusCircledIcon width={16} height={16} color="#5E95ED" style={{ display: 'inline', verticalAlign: 'middle', transform: 'translateY(-2px)' }} /> next to a language (limit 10MB)
                      </div>
                      <div className="text-xs opacity-80">
                        <span className="text-black">Supported Formats: CSV, TSV | </span>
                        <span className="text-[#0000FF] cursor-pointer hover:underline" onClick={() => window.open('https://skydrive3m-my.sharepoint.com/:w:/g/personal/aasn6zz_mmm_com/IQCjavUkh3HVSaxlOtozWQRGAaWLaQYkpK833eSKhO8vDb8?e=rO9AfG', '_blank')}>Click for Guide to Glossary</span>
                        </div>
                    </div>
                    <button
                      type="button"
                      aria-label="Dismiss glossary hint"
                      onClick={() => setShowGlossaryHint(false)}
                      className="p-1 rounded-full hover:bg-yellow-100 flex items-center justify-center"
                    >
                      <Cross2Icon width={20} height={20} />
                    </button>
                  </div>
                );
              })()}
            </div>
          )}
        </motion.div>

        <div className={`flex flex-col gap-2 p-2 ${isMobile ? 'w-full' : 'w-[45%]'}`}>
          {/* Step 2: Choose Languages */}
          <motion.div 
            className="flex flex-col gap-2 flex-1 w-inherit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {renderCollapse('languageSelection', isCollapsed.languageSelection)}

            {!isCollapsed.languageSelection && (
              <div
                className={awaitingFileSelection ? 'pointer-events-none opacity-50 select-none' : ''}
                title={awaitingFileSelection ? 'Please wait for the file to finish uploading' : undefined}
                aria-disabled={awaitingFileSelection || undefined}
              >
                <LanguagePicker
                  value={targetLanguages}
                  onChange={handleTargetLanguageChange}
                  onSelectionLimitExceeded={(message) => setSelectionWarning(message)}
                  onGlossaryFileTooLarge={(title, message) => setTranslationWarning?.({ title, message })}
                  onMultipleGlossaryFiles={(warning) => setTranslationWarning?.(warning)}
                  chatPage={chatPage}
                  maxSelections={2}
                />
              </div>
              )
            }
          </motion.div>

          {/* Mobile: Language selection full-screen menu */}
          {isMobile && isMobileLangMenuOpen && (
            <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={() => setIsMobileLangMenuOpen(false)}>
              {/* Backdrop */}
              <div className="absolute inset-0 bg-black bg-opacity-50" />

              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="relative w-full bg-[var(--color-secondary)] rounded-t-2xl shadow-2xl p-3"
                onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center">
                    <span className="text-sm font-semibold text-[var(--color-text)]">Select Language</span>
                    <button
                      type="button"
                      aria-label="Close language selector"
                      onClick={() => setIsMobileLangMenuOpen(false)}
                      className="ml-auto inline-flex items-center justify-center w-8 h-8 text-[#5E95ED]"
                    >
                      <Cross2Icon width={18} height={18} />
                    </button>
                  </div>

                  <LanguagePicker
                    value={targetLanguages}
                    onChange={handleTargetLanguageChange}
                    onSelectionLimitExceeded={(message) => setSelectionWarning(message)}
                    onGlossaryFileTooLarge={(title, message) => setTranslationWarning?.({ title, message })}
                    onMultipleGlossaryFiles={(warning) => setTranslationWarning?.(warning)}
                    chatPage={chatPage}
                    maxSelections={2}
                  />
              </motion.div>
            </div>
          )}
      
          {/* Step 3: Translate - only show when languages are selected */}
          <motion.div 
            className="flex flex-col gap-2 flex-1 w-inherit"
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <button
              type="button"
              onClick={handleTranslateDocuments}
              disabled={!canTranslate}
              className="inline-flex justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border border-[#5E95ED] bg-[#5E95ED] hover:bg-[var(--hover)] text-white disabled:cursor-not-allowed disabled:bg-gray-400 disabled:border-gray-500"
              style={{ 
                opacity: !canTranslate ? 0.6 : 1,
                cursor: !canTranslate ? 'not-allowed' : 'pointer',
              }}
              aria-label="Translate documents"
            >
              {isTranslating ? (
                <>
                  <div 
                    className="animate-spin"
                    style={{
                      width: 16,
                      height: 16,
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%'
                    }}
                  />
                  <span>Translating...</span>
                </>
              ) : (
                <span>Translate</span>
              )}
            </button>
          </motion.div>
        </div>
      </div>
      


      {/* Hidden file input for translation mode */}
      <input
        ref={translationFileInputRef}
        type="file"
        multiple
        accept=".txt,.pdf,.csv,.docx,.pptx,.xlsx,.msg,"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '1px',
          height: '1px',
          opacity: 0.01,
        }}
        onChange={handleFileChange}
      />
    </motion.div>
  );
};
